import { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { isBefore } from "date-fns";
import { MoreVertical, Palette } from "lucide-react";
import Button from "../../../components/Buttons/Button";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_CANCEL_SUBSCRIPTION,
  BUTTON_CHANGE_PAYMENT_METHODS,
  BUTTON_REACTIVATE_SUBSCRIPTION,
  FEATURE_ADDON_BILLING_CANCELS,
  FEATURE_ADDON_BILLING_MENU,
  FEATURE_ADDON_BILLING_RENEWS,
  FEATURE_ADDON_PAYWALL_PER_MONTH,
  FEATURE_ADDON_UNPAID_TITLE,
  ACTIVATE,
  WHITE_LABEL_ALREADY_ACTIVE,
  WHITE_LABEL_COMPLETE_BRAND,
  WHITE_LABEL_EDIT_BRAND,
  WHITE_LABEL_ONLY_OWNER,
  WHITE_LABEL_STATUS_ACTIVE,
  WHITE_LABEL_STATUS_CONFIGURING,
  WHITE_LABEL_STATUS_PENDING_BRANDING,
  WHITE_LABEL_STATUS_REVERT_PENDING,
  WHITE_LABEL_TITLE,
} from "../../../i18n/keysTranslations";
import {
  WHITE_LABEL_STATUS,
  getWhiteLabelBillingView,
} from "../../../utils/whiteLabelAddon";

const STATUS_LABEL_KEY = {
  [WHITE_LABEL_STATUS.PENDING_BRANDING]: WHITE_LABEL_STATUS_PENDING_BRANDING,
  [WHITE_LABEL_STATUS.CONFIGURING]: WHITE_LABEL_STATUS_CONFIGURING,
  [WHITE_LABEL_STATUS.ACTIVE]: WHITE_LABEL_STATUS_ACTIVE,
  [WHITE_LABEL_STATUS.REVERT_PENDING]: WHITE_LABEL_STATUS_REVERT_PENDING,
};

const WhiteLabelBillingRow = ({
  status,
  busy = false,
  buying = false,
  canManage = false,
  reserveMenu = false,
  onBuy,
  onEditBrand,
  onChangePaymentMethod,
  onCancel,
  onReactivate,
}) => {
  const { t } = useTranslationApp();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const view = getWhiteLabelBillingView({ status, canManage });
  const price = Number(view.priceMonthly) || 0;
  const badgeKey = view.grandfathered
    ? WHITE_LABEL_ALREADY_ACTIVE
    : STATUS_LABEL_KEY[view.setupStatus];
  const cancelScheduled =
    view.cancelDateTime != null &&
    isBefore(new Date(), new Date(Number(view.cancelDateTime)));
  const scheduleDate = cancelScheduled ? view.cancelDateTime : view.renewDate;
  const showSchedule =
    Boolean(scheduleDate) && view.showSubscriptionMenu && !view.unpaid;
  const ownerTooltip = t(WHITE_LABEL_ONLY_OWNER);
  const unpaidLabel = view.unpaid
    ? t(FEATURE_ADDON_UNPAID_TITLE, { feature: t(WHITE_LABEL_TITLE), tier: "" })
        .replace(/\s+/g, " ")
        .trim()
    : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        alignItems: "center",
        columnGap: 2,
        rowGap: 1.5,
        px: 2,
        py: 1.75,
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 4,
          backgroundColor: "rgba(0,0,0,0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#111827",
        }}
      >
        <Palette size={22} color="#111827" strokeWidth={2} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            {t(WHITE_LABEL_TITLE)}
          </Typography>
          {badgeKey ? (
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color:
                  view.setupStatus === WHITE_LABEL_STATUS.ACTIVE ||
                  view.grandfathered
                    ? "#1B7F4E"
                    : "text.secondary",
                backgroundColor:
                  view.setupStatus === WHITE_LABEL_STATUS.ACTIVE ||
                  view.grandfathered
                    ? "#E7F7EF"
                    : "rgba(0,0,0,0.05)",
                borderRadius: "999px",
                px: 1,
                py: 0.25,
              }}
            >
              {t(badgeKey)}
            </Typography>
          ) : null}
        </Box>
        {showSchedule ? (
          <Typography variant="body2" color="text.secondary">
            {t(
              cancelScheduled
                ? FEATURE_ADDON_BILLING_CANCELS
                : FEATURE_ADDON_BILLING_RENEWS,
              { date: new Date(Number(scheduleDate)) }
            )}
          </Typography>
        ) : null}
        {unpaidLabel ? (
          <Typography variant="body2" color="error">
            {unpaidLabel}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
          flexShrink: 0,
          width: { xs: "100%", sm: "auto" },
          order: { xs: 2, sm: 0 },
        }}
      >
        {price > 0 && !view.grandfathered && !view.canBuy ? (
          <Box
            sx={{
              textAlign: { xs: "left", sm: "right" },
              minWidth: { sm: 72 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography sx={{ fontWeight: 700 }}>${price}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t(FEATURE_ADDON_PAYWALL_PER_MONTH)}
              </Typography>
            </Box>
          </Box>
        ) : null}
        {view.unpaid && view.showSubscriptionMenu ? (
          <Button
            color="primary"
            disabled={!canManage || busy}
            tooltip={canManage ? "" : ownerTooltip}
            onClick={onChangePaymentMethod}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {t(BUTTON_CHANGE_PAYMENT_METHODS)}
          </Button>
        ) : null}
        {view.canBuy ? (
          <Button
            color="secondary"
            loading={buying}
            disabled={view.buyDisabled || busy || buying}
            tooltip={view.buyDisabled ? ownerTooltip : ""}
            onClick={onBuy}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {t(ACTIVATE)}
          </Button>
        ) : null}
        {view.showCompleteBrand || view.showEditBrand ? (
          <Button
            color="secondary"
            disabled={view.brandDisabled || busy}
            tooltip={view.brandDisabled ? ownerTooltip : ""}
            onClick={onEditBrand}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {t(
              view.showCompleteBrand
                ? WHITE_LABEL_COMPLETE_BRAND
                : WHITE_LABEL_EDIT_BRAND
            )}
          </Button>
        ) : null}
      </Box>
      {view.showSubscriptionMenu ? (
        <>
          <IconButton
            aria-label={t(FEATURE_ADDON_BILLING_MENU)}
            disabled={busy}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              ml: { xs: "auto", sm: 0 },
              order: { xs: 1, sm: 0 },
            }}
          >
            {busy ? <CircularProgress size={18} /> : <MoreVertical size={18} />}
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              disabled={busy || !canManage}
              onClick={() => {
                setMenuAnchor(null);
                onChangePaymentMethod();
              }}
            >
              {t(BUTTON_CHANGE_PAYMENT_METHODS)}
            </MenuItem>
            {cancelScheduled ? (
              <MenuItem
                disabled={busy || !canManage}
                onClick={() => {
                  setMenuAnchor(null);
                  onReactivate();
                }}
              >
                {t(BUTTON_REACTIVATE_SUBSCRIPTION)}
              </MenuItem>
            ) : (
              <MenuItem
                disabled={busy || !canManage}
                onClick={() => {
                  setMenuAnchor(null);
                  onCancel(view.renewDate || view.cancelDateTime);
                }}
              >
                {t(BUTTON_CANCEL_SUBSCRIPTION)}
              </MenuItem>
            )}
          </Menu>
        </>
      ) : reserveMenu ? (
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            display: { xs: "none", sm: "block" },
          }}
        />
      ) : null}
    </Box>
  );
};

export default WhiteLabelBillingRow;
